/**
 * ポータル: 社内マニュアル（船員アプリ）
 * タイトル・全文・タグで検索して読む。既読必須のものは「読みました」を記録する。編集は陸上から。
 */
import { useCallback, useEffect, useState } from 'react';
import { AppHeader } from '../../components/AppHeader';
import { PIcon } from '../../components/PIcon';
import { BottomNav } from '../../components/BottomNav';
import { apiFetch } from '../../services/api';
import { PillBadge, MarkdownView, fmtDateTime, fileUrl, inputStyle, EmptyState } from './ui';

interface Manual {
  id: string; kind: string; title: string; body?: string | null; file_name: string | null;
  tags: string[]; category: string | null; require_read: number; version: number;
  updated_at: string; my_read: boolean; excerpt?: string;
}

export default function PortalManualsPage() {
  const [rows, setRows] = useState<Manual[]>([]);
  const [q, setQ] = useState('');
  const [scope, setScope] = useState<'title' | 'full'>('title');
  const [tag, setTag] = useState('');
  const [tags, setTags] = useState<string[]>([]);
  const [detail, setDetail] = useState<Manual | null>(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (q) { params.set('q', q); params.set('scope', scope); }
      if (tag) params.set('tag', tag);
      const r = await apiFetch<{ data: Manual[] }>(`/portal/manuals?${params}`);
      setRows(r.data ?? []);
    } catch { /* noop */ }
    finally { setLoading(false); }
  }, [q, scope, tag]);

  useEffect(() => { void load(); }, [load]);
  useEffect(() => {
    apiFetch<{ data: { tags: string[] } }>('/portal/manuals/tags').then(r => setTags(r.data.tags ?? [])).catch(() => { /* noop */ });
  }, []);

  const open = async (id: string) => {
    try { const r = await apiFetch<{ data: Manual }>(`/portal/manuals/${id}`); setDetail(r.data); }
    catch (e) { alert(e instanceof Error ? e.message : '取得に失敗しました'); }
  };

  const markRead = async (m: Manual) => {
    try {
      await apiFetch(`/portal/manuals/${m.id}/read`, { method: 'POST', body: JSON.stringify({}) });
      setDetail(d => d ? { ...d, my_read: true } : d);
      setRows(rs => rs.map(r => r.id === m.id ? { ...r, my_read: true } : r));
    } catch { /* noop */ }
  };

  return (
    <div style={{ minHeight: '100dvh', display: 'flex', flexDirection: 'column', background: '#f8fafc' }}>
      <AppHeader title="社内マニュアル">
        <div style={{ display: 'flex', gap: 6 }}>
          <select style={{ ...inputStyle, width: 94, padding: '10px 6px' }} value={scope} onChange={e => setScope(e.target.value as 'title' | 'full')}>
            <option value="title">タイトル</option>
            <option value="full">全文</option>
          </select>
          <input style={{ ...inputStyle, flex: 1 }} value={q} onChange={e => setQ(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') void load(); }} placeholder="検索" />
          <button onClick={() => void load()}
            style={{ border: 'none', background: '#0f766e', color: '#fff', borderRadius: 10, padding: '0 14px', fontSize: 14, fontWeight: 800, cursor: 'pointer' }}>
            <PIcon name="search" size={16} color="#fff" />
          </button>
        </div>
        {tags.length > 0 && (
          <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap', marginTop: 8 }}>
            {tags.map(t => (
              <button key={t} onClick={() => setTag(tag === t ? '' : t)}
                style={{
                  border: '1px solid', borderColor: tag === t ? '#0f766e' : '#e2e8f0', borderRadius: 999,
                  padding: '4px 12px', fontSize: 12, cursor: 'pointer',
                  background: tag === t ? '#e6f4f1' : '#fff', color: tag === t ? '#0f766e' : '#64748b', fontWeight: 700,
                }}>{t}</button>
            ))}
          </div>
        )}
      </AppHeader>

      <div style={{ flex: 1, overflowY: 'auto', padding: '12px 14px 90px' }}>
        {loading ? (
          <p style={{ textAlign: 'center', color: '#64748b', fontSize: 14 }}>読み込み中...</p>
        ) : rows.length === 0 ? (
          <EmptyState img="/portal/empty-manuals.png" text="該当するマニュアルがありません" />
        ) : rows.map(m => (
          <button key={m.id} onClick={() => void open(m.id)}
            style={{
              display: 'block', width: '100%', textAlign: 'left', marginBottom: 10,
              background: '#fff', border: '1.5px solid #e4e9f0', borderRadius: 14, padding: '13px 14px', cursor: 'pointer',
            }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
              <PIcon name={m.kind === 'file' ? 'filetext' : 'book'} size={17} color="#0c5f59" />
              <span style={{ fontSize: 14.5, fontWeight: 700, color: '#0f172a', flex: 1 }}>{m.title}</span>
              {m.require_read === 1 && !m.my_read && <PillBadge color="#dc2626" bg="#fee2e2">要確認</PillBadge>}
            </div>
            <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap', alignItems: 'center' }}>
              {m.category && <PillBadge color="#334155" bg="#f1f5f9">{m.category}</PillBadge>}
              {m.tags.map(t => <span key={t} style={{ fontSize: 11, color: '#64748b' }}>#{t}</span>)}
              <span style={{ marginLeft: 'auto', fontSize: 11, color: '#64748b' }}>v{m.version} ・ {fmtDateTime(m.updated_at)}</span>
            </div>
          </button>
        ))}
      </div>

      {detail && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 1000, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'flex-end' }}
          onClick={() => setDetail(null)}>
          <div onClick={e => e.stopPropagation()}
            style={{ background: '#fff', borderRadius: '18px 18px 0 0', width: '100%', maxHeight: '88dvh', overflowY: 'auto', padding: '18px 18px 28px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 8 }}>
              <PIcon name={detail.kind === 'file' ? 'filetext' : 'book'} size={17} color="#0c5f59" />
              <span style={{ fontSize: 16, fontWeight: 800, color: '#0f172a', flex: 1 }}>{detail.title}</span>
              <button onClick={() => setDetail(null)} style={{ border: 'none', background: 'none', fontSize: 18, color: '#64748b', cursor: 'pointer' }}>✕</button>
            </div>
            {detail.kind === 'file' ? (
              <a href={fileUrl(`/portal/manuals/${detail.id}/file`)} target="_blank" rel="noreferrer"
                style={{ display: 'block', textAlign: 'center', background: '#0f766e', color: '#fff', borderRadius: 12, padding: '13px', fontSize: 14.5, fontWeight: 800, textDecoration: 'none', marginBottom: 12 }}>
                ファイルを開く（{detail.file_name}）
              </a>
            ) : (
              <div style={{ background: '#f8fafc', borderRadius: 12, padding: '12px 14px', marginBottom: 12 }}>
                <MarkdownView text={detail.body ?? ''} />
              </div>
            )}
            {detail.my_read ? (
              <div style={{ fontSize: 13.5, color: '#15803d', fontWeight: 700 }}>✓ 既読</div>
            ) : (
              <button onClick={() => void markRead(detail)}
                style={{ width: '100%', border: 'none', background: '#15803d', color: '#fff', borderRadius: 12, padding: '13px', fontSize: 14.5, fontWeight: 800, cursor: 'pointer' }}>
                読みました（既読にする）
              </button>
            )}
          </div>
        </div>
      )}

      <BottomNav />
    </div>
  );
}
